import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Chip,
    Box,
    Typography,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    ListSubheader,
    IconButton,
    Tooltip,
    ThemeProvider,
    createTheme,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { AuditLog } from '../../types/auditLog';
import api from '../../services/api';

interface AuditLogsTableProps {
    userId?: number;
}

const actionColors: { [key: string]: string } = {
    // User Management Events
    USER_EXPIRED: 'error',
    USER_NEVER_EXPIRE: 'success',
    USER_MADE_ADMIN: 'info',
    USER_REMOVED_ADMIN: 'warning',
    USER_CREATED: 'success',
    USER_DELETED: 'error',
    USER_UPDATED: 'info',
    USER_PASSWORD_CHANGED: 'warning',
    USER_PASSWORD_RESET: 'warning',

    // Document Events
    DOCUMENT_CREATED: 'success',
    DOCUMENT_UPDATED: 'info',
    DOCUMENT_DELETED: 'error',
    DOCUMENT_SHARED: 'info',
    DOCUMENT_UNSHARED: 'warning',
    DOCUMENT_EXPORTED: 'success',
    DOCUMENT_IMPORTED: 'success',

    // Authentication Events
    LOGIN_SUCCESS: 'success',
    LOGIN_FAILED: 'error',
    LOGOUT: 'info',
    SESSION_EXPIRED: 'warning',
    ACCOUNT_LOCKED: 'error',
    ACCOUNT_UNLOCKED: 'success',

    // System Events
    SYSTEM_BACKUP: 'success',
    SYSTEM_RESTORE: 'warning',
    SYSTEM_CONFIG_CHANGED: 'info',
    SYSTEM_MAINTENANCE: 'warning',
    SYSTEM_ERROR: 'error'
};

const formatDate = (dateString: string) => {
    // Handle case where dateString is an array (from LocalDateTime)
    if (Array.isArray(dateString)) {
        const [year, month, day, hour, minute, second] = dateString;
        return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
    }
    
    // Handle case where dateString is a string
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
};

const theme = createTheme();

const eventGroupActions: { [key: string]: { value: string; label: string }[] } = {
    USER_MANAGEMENT: [
        { value: 'USER_EXPIRED', label: 'User Expired' },
        { value: 'USER_NEVER_EXPIRE', label: 'Never Expire' },
        { value: 'USER_MADE_ADMIN', label: 'Made Admin' },
        { value: 'USER_REMOVED_ADMIN', label: 'Removed Admin' },
        { value: 'USER_CREATED', label: 'User Created' },
        { value: 'USER_DELETED', label: 'User Deleted' },
        { value: 'USER_UPDATED', label: 'User Updated' },
        { value: 'USER_PASSWORD_CHANGED', label: 'Password Changed' },
        { value: 'USER_PASSWORD_RESET', label: 'Password Reset' }
    ],
    DOCUMENT_EVENTS: [
        { value: 'DOCUMENT_CREATED', label: 'Document Created' },
        { value: 'DOCUMENT_UPDATED', label: 'Document Updated' },
        { value: 'DOCUMENT_DELETED', label: 'Document Deleted' },
        { value: 'DOCUMENT_SHARED', label: 'Document Shared' },
        { value: 'DOCUMENT_UNSHARED', label: 'Document Unshared' },
        { value: 'DOCUMENT_EXPORTED', label: 'Document Exported' },
        { value: 'DOCUMENT_IMPORTED', label: 'Document Imported' },
        { value: 'DOCUMENT_MERGED', label: 'Document Merged' }
    ],
    AUTHENTICATION: [
        { value: 'LOGIN_SUCCESS', label: 'Login Success' },
        { value: 'LOGIN_FAILED', label: 'Login Failed' },
        { value: 'LOGOUT', label: 'Logout' },
        { value: 'SESSION_EXPIRED', label: 'Session Expired' },
        { value: 'ACCOUNT_LOCKED', label: 'Account Locked' },
        { value: 'ACCOUNT_UNLOCKED', label: 'Account Unlocked' }
    ],
    SYSTEM: [
        { value: 'SYSTEM_BACKUP', label: 'System Backup' },
        { value: 'SYSTEM_RESTORE', label: 'System Restore' },
        { value: 'SYSTEM_CONFIG_CHANGED', label: 'Config Changed' },
        { value: 'SYSTEM_MAINTENANCE', label: 'Maintenance' },
        { value: 'SYSTEM_ERROR', label: 'System Error' }
    ]
};

const AuditLogsTable: React.FC<AuditLogsTableProps> = ({ userId }) => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalElements, setTotalElements] = useState(0);
    const [selectedAction, setSelectedAction] = useState<string>('');
    const [selectedEventGroup, setSelectedEventGroup] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [loading, setLoading] = useState(true);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            let url = `/admin/audit-logs?page=${page}&size=${rowsPerPage}`;
            
            if (searchTerm) {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            } else if (startDate && endDate) {
                url += `&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
            } else if (userId) {
                url += `&userId=${userId}`;
            } else if (selectedAction) {
                url += `&action=${encodeURIComponent(selectedAction)}`;
            } else if (selectedEventGroup) {
                url += `&eventGroup=${encodeURIComponent(selectedEventGroup)}`;
            }

            console.log('Fetching audit logs from:', url);
            const response = await api.get(url);
            console.log('Received audit logs data:', response.data);
            
            setLogs(response.data.content || []);
            setTotalElements(response.data.totalElements || 0);
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            setLogs([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [page, rowsPerPage, userId, selectedAction, selectedEventGroup, searchTerm, startDate, endDate]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleEventGroupChange = (event: SelectChangeEvent<string>) => {
        setSelectedEventGroup(event.target.value);
        setSelectedAction('');
        setPage(0);
    };

    const handleActionChange = (event: SelectChangeEvent<string>) => {
        setSelectedAction(event.target.value);
        setPage(0);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setPage(0);
    };

    const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStartDate(event.target.value);
        setPage(0);
    };

    const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEndDate(event.target.value);
        setPage(0);
    };

    const handleClearDates = () => {
        setStartDate('');
        setEndDate('');
        setPage(0);
    };

    const getAvailableActions = () => {
        if (!selectedEventGroup) {
            // Return all actions grouped by event group
            return Object.entries(eventGroupActions).flatMap(([group, actions]) => [
                <ListSubheader key={group}>{group.replace('_', ' ')}</ListSubheader>,
                ...actions.map(action => (
                    <MenuItem key={action.value} value={action.value}>
                        {action.label}
                    </MenuItem>
                ))
            ]);
        }
        
        // Return only actions for the selected event group
        return eventGroupActions[selectedEventGroup]?.map(action => (
            <MenuItem key={action.value} value={action.value}>
                {action.label}
            </MenuItem>
        )) || [];
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        endAdornment: searchTerm && (
                            <IconButton size="small" onClick={handleClearSearch}>
                                <ClearIcon />
                            </IconButton>
                        ),
                    }}
                    sx={{ minWidth: 200 }}
                />
                <TextField
                    label="Start Date"
                    type="datetime-local"
                    value={startDate}
                    onChange={handleStartDateChange}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                />
                <TextField
                    label="End Date"
                    type="datetime-local"
                    value={endDate}
                    onChange={handleEndDateChange}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                />
                {(startDate || endDate) && (
                    <IconButton size="small" onClick={handleClearDates}>
                        <ClearIcon />
                    </IconButton>
                )}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Event Group</InputLabel>
                    <Select
                        value={selectedEventGroup}
                        label="Event Group"
                        onChange={handleEventGroupChange}
                    >
                        <MenuItem value="">All Groups</MenuItem>
                        <MenuItem value="USER_MANAGEMENT">User Management</MenuItem>
                        <MenuItem value="DOCUMENT_EVENTS">Document Events</MenuItem>
                        <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
                        <MenuItem value="SYSTEM">System</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Action</InputLabel>
                    <Select
                        value={selectedAction}
                        label="Action"
                        onChange={handleActionChange}
                    >
                        <MenuItem value="">All Actions</MenuItem>
                        {getAvailableActions()}
                    </Select>
                </FormControl>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Event Group</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Details</TableCell>
                            <TableCell>IP Address</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Loading...
                                </TableCell>
                            </TableRow>
                        ) : logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No audit logs found
                                </TableCell>
                            </TableRow>
                        ) : (
                            logs.map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell>{formatDate(log.timestamp)}</TableCell>
                                    <TableCell>{log.username}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={log.eventGroup.replace('_', ' ')} 
                                            size="small"
                                            color={
                                                log.eventGroup === 'USER_MANAGEMENT' ? 'primary' :
                                                log.eventGroup === 'DOCUMENT_EVENTS' ? 'success' :
                                                log.eventGroup === 'AUTHENTICATION' ? 'warning' :
                                                'default'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>{log.action}</TableCell>
                                    <TableCell>{log.details}</TableCell>
                                    <TableCell>{log.ipAddress}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalElements}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};

export default AuditLogsTable; 